import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, money, phoneFmt } from '../api';
import { useApi } from '../useApi';
import { Empty, ErrorBox, Loading, Tile, useToast } from '../ui';

type Debtors = {
  items: Array<{ id: string; code: string; name: string; phone: string; cachedBalanceKop: string; cachedBottles: number; creditBlocked: boolean }>;
  totalKop: string;
};
type Bottles = {
  atClientsCached: number;
  atClientsLedger: number;
  inSync: boolean;
  issued: number;
  returned: number;
  lost: number;
  top: Array<{ id: string; code: string; name: string; cachedBottles: number; phone: string }>;
};
type Consistency = { mismatched: number; items: Array<{ id: string; code: string; name: string; cachedKop: string; ledgerKop: string; cachedBottles: number; ledgerBottles: number }> };

export function Reports() {
  const [tab, setTab] = useState<'debt' | 'bottles' | 'consistency'>('debt');
  const debtors = useApi<Debtors>(tab === 'debt' ? '/reports/debtors?limit=200' : null);
  const bottles = useApi<Bottles>(tab === 'bottles' ? '/reports/bottles' : null);
  const cons = useApi<Consistency>(tab === 'consistency' ? '/reports/consistency' : null);
  const toast = useToast();

  return (
    <>
      <h1>Отчёты</h1>
      <div className="tabs">
        <button className={tab === 'debt' ? 'active' : ''} onClick={() => setTab('debt')}>
          Долги
        </button>
        <button className={tab === 'bottles' ? 'active' : ''} onClick={() => setTab('bottles')}>
          Оборот тары
        </button>
        <button className={tab === 'consistency' ? 'active' : ''} onClick={() => setTab('consistency')}>
          Сверка
        </button>
      </div>

      {tab === 'debt' ? (
        <div className="card">
          {debtors.loading ? (
            <Loading />
          ) : debtors.error ? (
            <ErrorBox error={debtors.error} />
          ) : !debtors.data?.items.length ? (
            <Empty>Должников нет</Empty>
          ) : (
            <>
              <div className="grid c3" style={{ marginBottom: 16 }}>
                <Tile label="Всего долга" value={money(debtors.data.totalKop)} tone="debt" />
                <Tile label="Должников" value={debtors.data.items.length} />
                <Tile
                  label="Крупнейший"
                  value={money(-BigInt(debtors.data.items[0]!.cachedBalanceKop))}
                  sub={debtors.data.items[0]!.name}
                />
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Код</th>
                    <th>Клиент</th>
                    <th>Телефон</th>
                    <th className="num">Долг</th>
                    <th className="num">Тара</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {debtors.data.items.map((c) => (
                    <tr key={c.id}>
                      <td className="mono muted">{c.code}</td>
                      <td>
                        <Link to={`/clients/${c.id}`}>{c.name}</Link>
                        {c.creditBlocked ? <span className="badge red" style={{ marginLeft: 6 }}>отсрочка закрыта</span> : null}
                      </td>
                      <td className="mono">{phoneFmt(c.phone)}</td>
                      <td className="num debt">{money(-BigInt(c.cachedBalanceKop))}</td>
                      <td className="num">{c.cachedBottles}</td>
                      <td>
                        <button
                          className="ghost"
                          onClick={async () => {
                            await api(`/clients/${c.id}`, {
                              method: 'PATCH',
                              body: { creditBlocked: !c.creditBlocked },
                            });
                            toast.show(c.creditBlocked ? 'Отсрочка открыта' : 'Отсрочка закрыта');
                            await debtors.reload();
                          }}
                        >
                          {c.creditBlocked ? 'открыть отсрочку' : 'закрыть отсрочку'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      ) : null}

      {tab === 'bottles' ? (
        <div className="card">
          {bottles.loading ? (
            <Loading />
          ) : bottles.data ? (
            <>
              <div className="grid c4" style={{ marginBottom: 16 }}>
                <Tile label="Тары у клиентов" value={bottles.data.atClientsCached} sub="по кэшу балансов" />
                <Tile
                  label="По журналу проводок"
                  value={bottles.data.atClientsLedger}
                  sub={bottles.data.inSync ? 'сходится' : 'РАСХОЖДЕНИЕ'}
                  tone={bottles.data.inSync ? undefined : 'debt'}
                />
                <Tile label="Выдано всего" value={bottles.data.issued} />
                <Tile label="Возвращено" value={bottles.data.returned} sub={`списано: ${bottles.data.lost}`} />
              </div>
              <h2>У кого больше всего тары</h2>
              <table>
                <thead>
                  <tr>
                    <th>Клиент</th>
                    <th>Телефон</th>
                    <th className="num">Бутылей</th>
                  </tr>
                </thead>
                <tbody>
                  {bottles.data.top.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/clients/${c.id}`}>{c.name}</Link>
                      </td>
                      <td className="mono">{phoneFmt(c.phone)}</td>
                      <td className="num">{c.cachedBottles}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </div>
      ) : null}

      {tab === 'consistency' ? (
        <div className="card">
          <p className="muted">
            Кэш баланса против журнала проводок. Журнал — источник истины; расхождение
            означает, что кэш не догнал, и лечится пересчётом.
          </p>
          {cons.loading ? (
            <Loading />
          ) : cons.data?.mismatched === 0 ? (
            <div className="ok">Расхождений нет — кэш и журнал сходятся у всех клиентов.</div>
          ) : (
            <>
              <button
                className="primary"
                onClick={async () => {
                  await api('/admin/recalc', { body: {} });
                  toast.show('Пересчитано');
                  await cons.reload();
                }}
              >
                Пересчитать всех
              </button>
              <table style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Клиент</th>
                    <th className="num">Кэш, ₽</th>
                    <th className="num">Журнал, ₽</th>
                    <th className="num">Кэш, тара</th>
                    <th className="num">Журнал, тара</th>
                  </tr>
                </thead>
                <tbody>
                  {cons.data?.items.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/clients/${c.id}`}>{c.name}</Link>
                      </td>
                      <td className="num">{money(c.cachedKop)}</td>
                      <td className="num">{money(c.ledgerKop)}</td>
                      <td className="num">{c.cachedBottles}</td>
                      <td className="num">{c.ledgerBottles}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      ) : null}
      {toast.node}
    </>
  );
}
